import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFloppyDisk,
  faPaperPlane,
  faArrowRight,
  faArrowLeft,
  faFilter,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import { faFloppyDisk as farFloppyDisk } from "@fortawesome/free-regular-svg-icons";
import { productService } from "../services/product";
import { orderService } from "../services/order";
import { productionService } from "../services/production";
import toast from "react-hot-toast"; // Importar toast

const NewProduction = () => {
  const [productsData, setProductsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [orderName, setOrderName] = useState(""); // Novo estado para o nome da ordem (opcional)
  const [orderedPeriod, setOrderedPeriod] = useState("30days"); // 'today', '7days', '30days', 'custom'
  const [consumptionPeriod, setConsumptionPeriod] = useState("last_year"); // 'last_year', '6months', '3months', 'custom'
  const [customOrderedStartDate, setCustomOrderedStartDate] = useState("");
  const [customConsumptionStartDate, setCustomConsumptionStartDate] = useState("");
  const [customConsumptionEndDate, setCustomConsumptionEndDate] = useState("");
  const [isOrderedFilterOpen, setIsOrderedFilterOpen] = useState(false);
  const [isConsumptionFilterOpen, setIsConsumptionFilterOpen] = useState(false);
  const [usingCache, setUsingCache] = useState(true); // Estado para controlar se está usando cache ou filtros

  const navigate = useNavigate();
  const location = useLocation();

  // Função para calcular litros necessários baseado na produtividade
  const calculateLiters = (kilos, produtividade) => {
    if (!produtividade || produtividade === 0) return 0;
    // Fórmula: litros = (kg / produtividade) * 100
    // Exemplo: 20kg com 20% produtividade = (20 / 20) * 100 = 100L
    return (kilos / produtividade) * 100;
  };

  // Função para formatar a exibição kg/L com cálculo dinâmico
  const formatKgLiters = (product, quantityToProduce) => {
    const kilos = quantityToProduce || 0;
    const produtividade = product.produtividade || 20;
    const liters = calculateLiters(kilos, produtividade) || 0;
    return `${(kilos || 0).toFixed(2)}kg / ${(liters || 0).toFixed(0)}L`;
  };

  // Função para gerar nome do rascunho automaticamente
  const generateDraftName = () => {
    const today = new Date();
    const dateStr = today.toLocaleDateString('pt-BR');
    return `Produção ${dateStr}`;
  };

  // Função para aplicar todas as sugestões
  const handleApplyAllSuggestions = () => {
    setProductsData(prevData =>
      prevData.map(product => ({
        ...product,
        quantityToProduce: product.suggestedQuantity
      }))
    );
    toast.success("Todas as sugestões foram aplicadas!");
  };

  // Função para aplicar filtros e recalcular
  const handleApplyFilters = async () => {
    setLoading(true);
    try {
      await fetchProducts(false); // false = recalcular com filtros
      toast.success("Sugestões recalculadas com os filtros aplicados!");
    } catch (err) {
      toast.error("Erro ao aplicar filtros");
    } finally {
      setLoading(false);
    }
  };

  // Função para voltar ao cache
  const handleResetToCache = async () => {
    setLoading(true);
    try {
      await fetchProducts(true); // true = carregar do cache
      toast.success("Valores do cache restaurados!");
    } catch (err) {
      toast.error("Erro ao carregar cache");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const draftId = params.get("draft_id");

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        if (draftId) {
          await loadDraft(draftId);
        } else {
          await fetchProducts();
        }
      } catch (err) {
        setError(err.message || "Erro ao carregar dados.");
        console.error("Erro ao carregar dados iniciais:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [location.search]);

  const fetchProducts = async (useCache = true) => {
    try {
      if (useCache) {
        // MODO CACHE: Carregar do cache (rápido, sem recalcular)
        console.log('📦 Carregando produtos do cache...');
        const response = await productionService.getProductionSuggestions();

        if (!response || !response.suggestions) {
          console.warn('⚠️  Nenhuma sugestão encontrada no cache');
          setProductsData([]);
          return;
        }

        const cachedSuggestions = response.suggestions;
        console.log(`✅ ${cachedSuggestions.length} produtos carregados do cache`);

        // Mapear sugestões do cache para o formato da página
        const dataWithAggregates = cachedSuggestions.map((suggestion) => ({
          id: suggestion.id,
          name: suggestion.name,
          product_code: suggestion.product_code,
          current_stock_kg: suggestion.current_stock_kg,
          min_stock_quantity: suggestion.min_stock_quantity,
          max_stock_quantity: suggestion.max_stock_quantity || 999999,
          curing_time_days: suggestion.curing_time_days,
          is_cured: suggestion.is_cured,
          produtividade: suggestion.produtividade,
          orderedQuantity: suggestion.orderedQuantity || 0,
          averageConsumption: suggestion.averageConsumption || 0,
          suggestedQuantity: Number(suggestion.suggestedQuantity ?? suggestion.suggested_quantity ?? 0),
          production_priority: suggestion.production_priority || 'ok',
          quantityToProduce: 0,
          formula: suggestion.formula || null,
          curableQuantity: suggestion.curableQuantity || 0,
          isCured: suggestion.is_cured || false,
          curingTimeDays: suggestion.curing_time_days || 0,
        }));

        console.log('📊 Produtos carregados do cache');
        // Ordenar produtos por nome em ordem alfabética
        const sortedData = Array.isArray(dataWithAggregates)
          ? dataWithAggregates.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          : [];
        setProductsData(sortedData);
        setUsingCache(true);
      } else {
        // MODO FILTROS: Recalcular com filtros aplicados
        console.log('🔄 Recalculando sugestões com filtros...');
        const products = await productService.getProducts();
        const nonVariantProducts = products.filter(product => !product.is_variant);

        // Calcular sugestões com filtros aplicados
        const suggestions = await productionService.calculateProductionSuggestions({
          orderedPeriod,
          consumptionPeriod,
          customOrderedStartDate,
          customConsumptionStartDate,
          customConsumptionEndDate
        });

        // Mapear sugestões aos produtos
        const suggestionMap = {};
        if (suggestions && suggestions.suggestions) {
          suggestions.suggestions.forEach(s => {
            suggestionMap[s.id] = s;
          });
        }

        const dataWithAggregates = nonVariantProducts.map((product) => {
          const suggestion = suggestionMap[product.id] || {
            orderedQuantity: 0,
            averageConsumption: 0,
            suggestedQuantity: 0,
            production_priority: 'ok'
          };

          return {
            ...product,
            orderedQuantity: suggestion.orderedQuantity || 0,
            averageConsumption: suggestion.averageConsumption || 0,
            suggestedQuantity: Number(suggestion.suggestedQuantity ?? suggestion.suggested_quantity ?? 0),
            production_priority: suggestion.production_priority || 'ok',
            quantityToProduce: 0,
            max_stock_quantity: product.max_stock_quantity || 999999,
            formula: suggestion.formula || null,
            curableQuantity: suggestion.curableQuantity || 0,
            isCured: product.is_cured || false,
            curingTimeDays: product.curing_time_days || 0,
          };
        });

        console.log('📊 Produtos recalculados com filtros');
        // Ordenar produtos por nome em ordem alfabética
        const sortedData = Array.isArray(dataWithAggregates)
          ? dataWithAggregates.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
          : [];
        setProductsData(sortedData);
        setUsingCache(false);
      }
    } catch (err) {
      console.error("Erro ao buscar produtos:", err);
      toast.error(err.message || "Erro ao carregar produtos");
      setProductsData([]);
    }
  };

  const loadDraft = async (draftId) => {
    try {
      toast.loading("Carregando rascunho...");
      const draft = await productionService.getProductionOrderDraftById(
        draftId
      );
      const draftData = draft.draft_data;

      setOrderName(draftData.orderName || "");
      setProductsData(Array.isArray(draftData.productsData) ? draftData.productsData : []);
      // Restaurar outros estados se necessário
      toast.dismiss();
      toast.success("Rascunho carregado com sucesso!");
    } catch (err) {
      toast.dismiss();
      toast.error(err.message || "Erro ao carregar rascunho.");
      // Se falhar ao carregar o rascunho, carrega a lista de produtos padrão
      await fetchProducts();
    }
  };

  const handleQuantityToProduceChange = (productId, value) => {
    setProductsData((prevData) =>
      prevData.map((product) =>
        product.id === productId
          ? { ...product, quantityToProduce: Number(value) || 0 }
          : product
      )
    );
  };

  const handleSuggestQuantity = (productId, suggestedQuantity) => {
    setProductsData((prevData) =>
      prevData.map((product) =>
        product.id === productId
          ? { ...product, quantityToProduce: suggestedQuantity }
          : product
      )
    );
  };

  const handleSaveDraft = async () => {

    const draftData = {
      orderName,
      productsData,
      // Salvar outros estados relevantes se houver
    };

    try {
      setLoading(true);
      await productionService.saveProductionOrderDraft({
        draft_name: generateDraftName(),
        draft_data: draftData,
      });
      toast.success("Rascunho guardado com sucesso!");
    } catch (err) {
      toast.error(err.message || "Erro ao guardar rascunho.");
    } finally {
      setLoading(false);
    }
  };

  const handlePublishOrder = async () => {
    console.log("handlePublishOrder: Função chamada.");
    console.log("handlePublishOrder: orderName =", orderName);
    console.log("handlePublishOrder: productsData =", productsData);

    const itemsToProduce = productsData.filter(
      (product) => product.quantityToProduce > 0
    );

    console.log("handlePublishOrder: itemsToProduce =", itemsToProduce);

    if (itemsToProduce.length === 0) {
      toast.error("Adicione pelo menos um produto para produzir.");
      return;
    }

    // Validação de stock máximo
    for (const item of itemsToProduce) {
      const currentProduct = productsData.find((p) => p.id === item.id);
      if (currentProduct) {
        const projectedStock =
          (currentProduct.current_stock_kg || 0) + item.quantityToProduce;
        if (projectedStock > currentProduct.max_stock_quantity) {
          toast.error(
            `Atenção: A produção de ${currentProduct.name} (${item.quantityToProduce} kg) fará com que o stock (${projectedStock} kg) ultrapasse o limite máximo (${currentProduct.max_stock_quantity} kg).`
          );
          return; // Impede a publicação da ordem
        }
      }
    }

    // Agora os campos são opcionais - serão gerados automaticamente pelo backend se não fornecidos
    const productionOrderData = {
      // Se orderName estiver vazio, será gerado automaticamente como "Nova Ordem + data atual"
      order_name: orderName.trim() || null,
      // order_number será gerado automaticamente
      // start_date será a data atual
      // expected_completion_date será data atual + 1 dia
      items: itemsToProduce.map((item) => ({
        product_id: item.id,
        suggested_quantity: item.suggestedQuantity,
        quantity_to_produce: item.quantityToProduce,
      })),
    };

    console.log(
      "handlePublishOrder: productionOrderData =",
      productionOrderData
    );

    setLoading(true);
    try {
      console.log(
        "Dados da Ordem de Produção a serem enviados:",
        productionOrderData
      );
      const result = await productionService.createProductionOrder(
        productionOrderData
      );
      console.log("handlePublishOrder: Resultado da criação =", result);
      toast.success("Ordem de Produção criada com sucesso!");
      navigate("/production");
    } catch (err) {
      console.error("handlePublishOrder: Erro detalhado =", err);
      toast.error(err.message || "Erro ao criar Ordem de Produção.");
      console.error("Erro ao criar Ordem de Produção:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button
            onClick={() => navigate("/production")}
            className="px-2 py-1 sm:px-3 sm:py-2 text-neutral-600 hover:text-neutral-900"
            title="Voltar"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="text-sm sm:text-base" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl">Nova Ordem de Produção</h1>
            <div className="flex items-center space-x-2 mt-1">
              {usingCache ? (
                <span className="text-xs text-green-600 flex items-center">
                  <span className="w-2 h-2 bg-green-600 rounded-full mr-1"></span>
                  Valores do cache
                </span>
              ) : (
                <>
                  <span className="text-xs text-blue-600 flex items-center">
                    <span className="w-2 h-2 bg-blue-600 rounded-full mr-1"></span>
                    Filtros personalizados
                  </span>
                  <button
                    onClick={handleResetToCache}
                    className="text-xs text-neutral-600 hover:text-neutral-900 underline flex items-center"
                  >
                    <FontAwesomeIcon icon={faSync} className="mr-1" />
                    Voltar ao cache
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
          <input
            type="text"
            placeholder="Nome da Ordem de Produção (Opcional)"
            value={orderName}
            onChange={(e) => setOrderName(e.target.value)}
            className="px-2 py-1 sm:px-4 sm:py-2 border border-neutral-200 rounded-lg text-xs sm:text-sm w-full sm:w-48"
          />
          <div className="flex space-x-2">
            <button
              onClick={handleSaveDraft}
              className="px-2 py-1 sm:px-4 sm:py-2 border border-neutral-300 rounded-lg hover:bg-neutral-50 text-xs sm:text-sm whitespace-nowrap"
            >
              <FontAwesomeIcon icon={farFloppyDisk} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
              Guardar
            </button>
            <button
              onClick={handlePublishOrder}
              className="px-2 py-1 sm:px-4 sm:py-2 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 text-xs sm:text-sm whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faPaperPlane} className="mr-1 sm:mr-2 text-xs sm:text-sm" />
              Publicar
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="w-full relative">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm sticky left-0 bg-neutral-50 z-10 min-w-[120px] md:min-w-0 md:static border-r border-neutral-200 md:border-r-0">Produto</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Stock Atual</th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">
                    <div className="flex items-center flex-col">
                      <div className="flex items-center text-xs sm:text-sm">
                        Encomendado
                        <button
                          onClick={() => setIsOrderedFilterOpen(!isOrderedFilterOpen)}
                          className="ml-1 sm:ml-2 p-1 hover:bg-neutral-100 rounded"
                        >
                          <FontAwesomeIcon icon={faFilter} className="text-neutral-900 text-xs sm:text-sm" />
                        </button>
                      </div>
                      {isOrderedFilterOpen && (
                        <div className="mt-1 sm:mt-2 bg-white shadow-lg rounded-lg border border-neutral-200 p-2 w-40 sm:w-48 z-50">
                          <div className="text-xs sm:text-sm mb-1 sm:mb-2">Período</div>
                          <div className="space-y-1 sm:space-y-2">
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="orderedPeriod"
                                value="today"
                                checked={orderedPeriod === "today"}
                                onChange={(e) => setOrderedPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              A partir de hoje
                            </label>
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="orderedPeriod"
                                value="7days"
                                checked={orderedPeriod === "7days"}
                                onChange={(e) => setOrderedPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Próximos 7 dias
                            </label>
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="orderedPeriod"
                                value="30days"
                                checked={orderedPeriod === "30days"}
                                onChange={(e) => setOrderedPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Próximos 30 dias
                            </label>
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="orderedPeriod"
                                value="custom"
                                checked={orderedPeriod === "custom"}
                                onChange={(e) => setOrderedPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Personalizado
                            </label>
                            {orderedPeriod === "custom" && (
                              <div className="border-t border-neutral-200 pt-1 sm:pt-2 mt-1 sm:mt-2">
                                <div className="text-xs sm:text-sm">Data Início</div>
                                <input
                                  type="date"
                                  value={customOrderedStartDate}
                                  onChange={(e) => setCustomOrderedStartDate(e.target.value)}
                                  className="mt-1 w-full px-1 sm:px-2 py-0.5 sm:py-1 border border-neutral-300 rounded text-xs sm:text-sm"
                                />
                              </div>
                            )}
                          </div>
                          <button
                            onClick={handleApplyFilters}
                            className="mt-2 w-full px-2 py-1 bg-neutral-900 text-white rounded text-xs sm:text-sm hover:bg-neutral-800"
                          >
                            Aplicar
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">
                    <div className="flex items-center flex-col">
                      <div className="flex items-center text-xs sm:text-sm">
                        Histórico de vendas
                        <button
                          onClick={() => setIsConsumptionFilterOpen(!isConsumptionFilterOpen)}
                          className="ml-1 sm:ml-2 p-1 hover:bg-neutral-100 rounded"
                        >
                          <FontAwesomeIcon icon={faFilter} className="text-neutral-900 text-xs sm:text-sm" />
                        </button>
                      </div>
                      {isConsumptionFilterOpen && (
                        <div className="mt-1 sm:mt-2 bg-white shadow-lg rounded-lg border border-neutral-200 p-2 w-40 sm:w-48 z-50">
                          <div className="text-xs sm:text-sm mb-1 sm:mb-2">Período Base</div>
                          <div className="space-y-1 sm:space-y-2">
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="consumptionPeriod"
                                value="last_year"
                                checked={consumptionPeriod === "last_year"}
                                onChange={(e) => setConsumptionPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Último ano
                            </label>
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="consumptionPeriod"
                                value="6months"
                                checked={consumptionPeriod === "6months"}
                                onChange={(e) => setConsumptionPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Últimos 6 meses
                            </label>
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="consumptionPeriod"
                                value="3months"
                                checked={consumptionPeriod === "3months"}
                                onChange={(e) => setConsumptionPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Últimos 3 meses
                            </label>
                            <label className="flex items-center text-xs sm:text-sm">
                              <input
                                type="radio"
                                name="consumptionPeriod"
                                value="custom"
                                checked={consumptionPeriod === "custom"}
                                onChange={(e) => setConsumptionPeriod(e.target.value)}
                                className="mr-1 sm:mr-2"
                              />
                              Personalizado
                            </label>
                            {consumptionPeriod === "custom" && (
                              <div className="border-t border-neutral-200 pt-1 sm:pt-2 mt-1 sm:mt-2">
                                <div className="text-xs sm:text-sm">Data Início</div>
                                <input
                                  type="date"
                                  value={customConsumptionStartDate}
                                  onChange={(e) => setCustomConsumptionStartDate(e.target.value)}
                                  className="mt-1 w-full px-1 sm:px-2 py-0.5 sm:py-1 border border-neutral-300 rounded text-xs sm:text-sm"
                                />
                                <div className="text-xs sm:text-sm mt-1 sm:mt-2">Data Fim</div>
                                <input
                                  type="date"
                                  value={customConsumptionEndDate}
                                  onChange={(e) => setCustomConsumptionEndDate(e.target.value)}
                                  className="mt-1 w-full px-1 sm:px-2 py-0.5 sm:py-1 border border-neutral-300 rounded text-xs sm:text-sm"
                                />
                              </div>
                            )}
                          </div>
                          <button
                            onClick={handleApplyFilters}
                            className="mt-2 w-full px-2 py-1 bg-neutral-900 text-white rounded text-xs sm:text-sm hover:bg-neutral-800"
                          >
                            Aplicar
                          </button>
                        </div>
                      )}
                    </div>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">
                    <div className="flex items-center text-xs sm:text-sm">
                      Sugestão
                      <button
                        className="ml-1 sm:ml-2 p-1 hover:bg-neutral-100 rounded"
                        onClick={handleApplyAllSuggestions}
                        title="Aplicar todas as sugestões"
                      >
                        <FontAwesomeIcon
                          icon={faArrowRight}
                          className="text-neutral-600 text-xs sm:text-sm"
                        />
                      </button>
                    </div>
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm">Produzir (L/kg)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {productsData.length > 0 ? (
                  productsData.map((product) => (
                    <tr key={product.id}>
                      <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm sticky left-0 bg-white z-10 min-w-[120px] md:min-w-0 md:static border-r border-neutral-100 md:border-r-0">
                        <div className="relative group">
                          <span className={product.formula ? 'cursor-help border-b border-dotted border-blue-400' : ''}>
                            {product.name}
                          </span>
                          {product.formula && (
                            <div className="hidden group-hover:block fixed bg-neutral-800 text-white text-xs rounded-lg p-3 shadow-2xl border border-neutral-600" style={{ minWidth: '320px', maxWidth: '400px', whiteSpace: 'normal', zIndex: 9999, marginTop: '-150px', marginLeft: '150px' }}>
                              <div className="font-semibold text-green-300 mb-2 text-sm border-b border-neutral-600 pb-2">
                                📊 Fórmula de Sugestão
                              </div>
                              <div className="font-semibold text-blue-300 mb-1">
                                {product.formula.name}
                              </div>
                              {product.formula.description && (
                                <div className="text-neutral-400 text-[10px] mb-2 italic">
                                  {product.formula.description}
                                </div>
                              )}
                              <div className="font-mono text-yellow-300 mb-3 bg-neutral-900 px-3 py-2 rounded border border-neutral-700">
                                {product.formula.expression}
                              </div>
                              <div className="mt-2 text-neutral-300 text-[11px] space-y-2">
                                <div className="font-semibold text-neutral-100 mb-3 text-xs">📝 Valores Utilizados:</div>
                                <div className="pl-1">• Estoque mínimo: <span className="text-white font-semibold">{product.min_stock_quantity || 0} kg</span></div>
                                <div className="pl-1">• Estoque atual: <span className="text-white font-semibold">{product.current_stock_kg || 0} kg</span></div>
                                <div className="pl-1">• Encomendas (30d): <span className="text-white font-semibold">{product.orderedQuantity || 0} kg</span></div>
                                {product.isCured && product.curingTimeDays > 0 && (
                                  <div className="text-yellow-300 pl-1">
                                    • Consumo na cura: <span className="font-semibold">{product.curableQuantity || 0} kg</span>
                                  </div>
                                )}
                                <div className="text-blue-300 font-semibold mt-4 text-sm border-t border-neutral-700 pt-3 bg-neutral-900 px-2 py-2 rounded">
                                  ✅ Resultado: {product.suggestedQuantity} kg
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                        <div>
                          <div>{product.current_stock_kg || 0} kg</div>
                          {product.is_cured && (
                            <div className="text-xs sm:text-sm text-neutral-500">
                              Em Cura: {product.curing_time_days} dias
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                        {product.orderedQuantity} kg
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                        {product.averageConsumption} kg/mês
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                        <div className="flex items-center">
                          <span className="mr-1 sm:mr-2 text-xs sm:text-sm">
                            {product.suggestedQuantity} kg
                          </span>
                          <button
                            onClick={() =>
                              handleSuggestQuantity(
                                product.id,
                                product.suggestedQuantity
                              )
                            }
                            className="p-1 hover:bg-neutral-100 rounded"
                            title="Aplicar sugestão"
                          >
                            <FontAwesomeIcon
                              icon={faArrowRight}
                              className="text-neutral-600 text-xs sm:text-sm"
                            />
                          </button>
                        </div>
                      </td>
                      <td className="px-2 sm:px-4 py-2 sm:py-4 text-xs sm:text-sm">
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={product.quantityToProduce}
                            onChange={(e) =>
                              handleQuantityToProduceChange(
                                product.id,
                                e.target.value
                              )
                            }
                            className="w-16 sm:w-24 px-1 sm:px-2 py-0.5 sm:py-1 border border-neutral-300 rounded text-xs sm:text-sm"
                            placeholder="0"
                          />
                          <span className="text-neutral-600 text-xs">
                            {formatKgLiters(product, product.quantityToProduce)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="7"
                      className="px-2 sm:px-4 py-2 sm:py-4 text-center text-neutral-500 text-xs sm:text-sm"
                    >
                      Nenhum produto encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewProduction;
